import {
  ConnectionModeEnum,
  ConnectionStatusEnum,
  IconName,
  ProductTypeEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  isExcludedByCurrencyOrBasePairFilters,
  productTypeLabel,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useSecuritiesContext,
  useSymbolsFilter,
  useTradingMarketAccountsFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type DateRangeFilter,
  type FilterableProperty,
} from '@talos/kyoko';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useFeatureFlag, useMarketsFilter } from '../../../hooks';
import type { MarketSecurityStatusLocal } from './types';

export interface SecurityMasterFilter extends DateRangeFilter {
  Statuses?: ConnectionStatusEnum[];
  MarketDataEnableds?: string[];
  Enableds?: ConnectionModeEnum[];
  MarketAccounts?: string[];
  Symbols?: string[];
  Markets?: string[];
  ProductTypes?: ProductTypeEnum[];
  Available?: string;
  Currencies?: string[];
  BasePairs?: string[];
}

export const useSecurityMasterFilter = ({
  initialFilter,
  saveFilter,
}: {
  initialFilter?: SecurityMasterFilter;
  saveFilter: (newFilter: any) => void;
}) => {
  const [filter, setFilter] = useState(() => cleanupInitialFilterDateRange(initialFilter));
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableSecurityMasterRequiredFiltering } = useFeatureFlag();

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const newFilter = action instanceof Function ? action(filter) : action;
      setFilter(newFilter);
      saveFilter(newFilter);
    },
    [filter, saveFilter]
  );

  const filterFunc = useCallback<BlotterTableClientLocalFilter<MarketSecurityStatusLocal>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }
      const security = securitiesBySymbol.get(data.Symbol);
      if (security && filterExistsAndExcludes(filter, 'ProductTypes', security, 'ProductType')) {
        return false;
      }
      // ProductType filtering is driven by data accessed on the security matching row symbol,
      // if the security is undefined, it has been deleted.
      // We decide to not show it when filtering on product type, since it is not matching (it is undefined)
      if (!security && filter['ProductTypes'] && filter['ProductTypes'] instanceof Array) {
        return false;
      }
      if (filter['MarketDataEnableds'] && filter['MarketDataEnableds'] instanceof Array) {
        if (data['_capabilities'].includes('MarketData') && !filter['MarketDataEnableds'].includes('true')) {
          return false;
        }
        if (!data['_capabilities'].includes('MarketData') && !filter['MarketDataEnableds'].includes('false')) {
          return false;
        }
      }

      if (filterExistsAndExcludes(filter, 'Statuses', data, 'Status')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Enableds', data, 'Enabled')) {
        return false;
      }

      if (filterExistsAndExcludes(filter, 'Available', data, '_marketEnabled')) {
        return false;
      }

      if (isExcludedByCurrencyOrBasePairFilters(data.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)) {
        return false;
      }

      return true;
    },
    [filter, securitiesBySymbol]
  );

  const tradingMarketAccountsFilter = useTradingMarketAccountsFilter({ includeInactiveAccounts: true });
  const symbolFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const marketsFilter = useMarketsFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () =>
      [
        {
          ...tradingMarketAccountsFilter,
          alwaysPresent: enableSecurityMasterRequiredFiltering,
          suppressInitialOpen: enableSecurityMasterRequiredFiltering,
        },
        {
          ...marketsFilter,
          alwaysPresent: enableSecurityMasterRequiredFiltering,
          suppressInitialOpen: enableSecurityMasterRequiredFiltering,
        },
        {
          key: 'Statuses',
          label: 'Status',
          icon: IconName.CheckCircle,
          options: Object.values(ConnectionStatusEnum),
          getOptionLabel: (option: string) => option,
        },
        {
          key: 'Enableds',
          label: 'Enabled',
          icon: IconName.CheckCircle,
          options: Object.values(ConnectionModeEnum),
          getOptionLabel: (option: string) => option,
        },
        {
          key: 'Available',
          label: 'Available',
          icon: IconName.CheckCircle,
          options: Object.values(ConnectionModeEnum),
          getOptionLabel: (option: string) => option,
        },
        {
          key: 'MarketDataEnableds',
          label: 'Market Data Enabled',
          icon: IconName.CheckCircle,
          options: ['true', 'false'],
          getOptionLabel: (option: string) => (option === 'true' ? 'enabled' : 'disabled'),
        },
        {
          key: 'ProductTypes',
          label: 'Product Type',
          icon: IconName.CubeTransparent,
          options: Object.values(ProductTypeEnum),
          getOptionLabel: (option: string) => productTypeLabel(option as ProductTypeEnum),
        },
        {
          ...symbolFilter,
          alwaysPresent: enableSecurityMasterRequiredFiltering,
          suppressInitialOpen: enableSecurityMasterRequiredFiltering,
        },
        currencyOfSymbolFilter,
        basePairFilter,
      ].compact(),
    [
      tradingMarketAccountsFilter,
      marketsFilter,
      symbolFilter,
      currencyOfSymbolFilter,
      basePairFilter,
      enableSecurityMasterRequiredFiltering,
    ]
  );

  return {
    filter,
    filterableProperties,
    changeFilter,
    filterFunc,
  };
};

export function colIDToFilterBuilderKey(id: string): keyof SecurityMasterFilter | undefined {
  if (id === 'ProductType') {
    return 'ProductTypes';
  }

  switch (id as keyof MarketSecurityStatusLocal) {
    case 'Symbol':
      return 'Symbols';
    case 'MarketAccount':
      return 'MarketAccounts';
    case 'Market':
      return 'Markets';
    case 'Status':
      return 'Statuses';
    case 'RequestedEnabled':
      return 'Enableds';
    case '_capabilities':
      return 'MarketDataEnableds';
    case '_marketEnabled':
      return 'Available';
  }
  return undefined;
}
